import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, LogIn, ShieldAlert } from "lucide-react";
import { AuthProvider } from "../context/AuthContext";
import { api, buildLoginUrl } from "../lib/api";
import Layout from "./Layout";
import PublicSiteLayout, { PageHero, PublicLinkButton } from "./PublicSiteLayout";

function AdminAccessScreen({ state, session }) {
  const isDenied = state === "denied";
  const adminLoginButton = (
    <a
      href={buildLoginUrl("streamer", "/app")}
      target="_top"
      rel="noopener"
      className="inline-flex items-center gap-2 px-5 py-3 rounded bg-[#9146ff] text-white font-medium hover:brightness-110 transition"
      data-testid="admin-twitch-login-button"
    >
      <LogIn className="w-4 h-4" /> Login with Twitch as Admin
    </a>
  );

  return (
    <PublicSiteLayout>
      <PageHero
        eyebrow="Admin dashboard"
        title={isDenied ? "Access denied." : "Streamer login required."}
        body={
          isDenied
            ? "You are signed in, but this dashboard is only available to the streamer/admin account."
            : "Admin dashboard access requires Twitch login with an allowlisted streamer account."
        }
        actions={
          isDenied ? (
            <>
              {adminLoginButton}
              <PublicLinkButton href="/">Back home</PublicLinkButton>
            </>
          ) : (
            adminLoginButton
          )
        }
      />

      <section className="mt-10 surface p-6 bg-black/75 max-w-3xl">
        <div className="flex items-start gap-3">
          {isDenied ? (
            <ShieldAlert className="w-5 h-5 text-[hsl(var(--currency-event))] mt-1" strokeWidth={1.5} />
          ) : (
            <Lock className="w-5 h-5 text-[hsl(var(--currency-event))] mt-1" strokeWidth={1.5} />
          )}
          <div>
            <h2 className="text-xl font-semibold text-[#f3ead3]">
              {isDenied ? "This account is not allowlisted." : "Use the admin Twitch flow."}
            </h2>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
              {isDenied
                ? "Try the admin Twitch login button above. If this account still lands here after admin login, it is not approved for dashboard access."
                : "This replaces the saved hidden OAuth link. If the signed-in Twitch account is not on the admin allowlist, the dashboard stays locked."}
            </p>
            {session && (
              <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
                Signed in as <span className="text-[#f3ead3]">{session.display_name || session.login || "viewer"}</span>.
              </p>
            )}
            {isDenied && (
              <Link to="/login" className="mt-5 inline-flex text-sm text-[hsl(var(--currency-event))] hover:underline">
                Switch accounts
              </Link>
            )}
          </div>
        </div>
      </section>
    </PublicSiteLayout>
  );
}

export default function AdminAppGate() {
  const [state, setState] = useState("loading");
  const [session, setSession] = useState(null);

  const loadSession = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get("/auth/me");
      if (!data?.authenticated) {
        setSession(null);
        setState("logged-out");
        return;
      }
      setSession(data);
      setState(data.is_admin || data.can_access_admin ? "allowed" : "denied");
    } catch (err) {
      setSession(null);
      setState("logged-out");
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  if (state === "loading") {
    return (
      <PublicSiteLayout>
        <section className="surface p-6 bg-black/75 max-w-3xl">
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Admin dashboard</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Checking admin session...</p>
        </section>
      </PublicSiteLayout>
    );
  }

  if (state === "denied") {
    return <AdminAccessScreen state="denied" session={session} />;
  }

  if (state !== "allowed") {
    return <AdminAccessScreen state="logged-out" />;
  }

  return (
    <AuthProvider>
      <Layout />
    </AuthProvider>
  );
}
